import { ClipboardEvent, KeyboardEvent } from 'react';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';
import { RadioGroup, RadioOption } from 'components/UI/V3/Forms/RadioGroup';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import clsx from 'clsx';
import { sanitizeIntInput } from '../utils';
import styles from './LoyaltyEditField.module.scss';

export interface LoyaltyEditFieldConfig {
    key: string;
    label: string;
    subtitle?: string;
    value: string;
    suffix?: string;
    type?: 'input' | 'select' | 'radio';
    options?: Array<SelectOption<undefined>>;
    radioOptions?: Array<RadioOption<string>>;
    fullWidth?: boolean;
    onChange?: (value: string) => void;
}

interface LoyaltyEditFieldProps {
    field: LoyaltyEditFieldConfig;
}

/**
 * Every text field in the Loyalty editors holds a whole number (points, days,
 * bookings), so typing and pasting anything but digits is rejected up front and
 * the emitted value is sanitized as a last resort.
 */
const blockNonDigitKeys = (event: KeyboardEvent<HTMLInputElement>) => {
    const isPrintableKey = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

    if (isPrintableKey && !/\d/.test(event.key)) {
        event.preventDefault();
    }
};

const blockNonDigitPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (!/^\d*$/.test(event.clipboardData.getData('text'))) {
        event.preventDefault();
    }
};

export const LoyaltyEditField = ({ field }: LoyaltyEditFieldProps) => {
    if (field.type === 'select') {
        return (
            <div className={clsx(styles.field, { [styles.fullWidth]: field.fullWidth })}>
                <Select
                    label={field.label}
                    options={field.options ?? []}
                    value={field.value}
                    onChange={value => field.onChange?.(value)}
                />
            </div>
        );
    }

    if (field.type === 'radio') {
        return (
            <div className={clsx(styles.field, { [styles.fullWidth]: field.fullWidth })}>
                <Label>{field.label}</Label>
                {field.subtitle && <p className={styles.subtitle}>{field.subtitle}</p>}
                <RadioGroup
                    value={field.value}
                    options={field.radioOptions ?? []}
                    onChange={value => field.onChange?.(value)}
                />
            </div>
        );
    }

    return (
        <div className={clsx(styles.field, { [styles.fullWidth]: field.fullWidth })}>
            <Input
                key={field.key}
                className={styles.input}
                label={field.label}
                value={field.value}
                autoFocus={false}
                inputMode="numeric"
                pattern="[0-9]*"
                endIcon={field.suffix ? <span className={styles.suffix}>{field.suffix}</span> : undefined}
                onKeyDown={blockNonDigitKeys}
                onPaste={blockNonDigitPaste}
                onChange={event => field.onChange?.(sanitizeIntInput(event.target.value))}
            />
        </div>
    );
};
